import { getThermalColumns } from '@/utils/thermalReceiptVolcora'
import { formatQuantityDisplay } from '@/composables/useQuantity'
import { formatMinorCurrency, tabCreditOnTabMinorFromSummary } from '@/utils/tabMoneyDisplay'
import { tabShowCustomerColumn } from '@/utils/tabParticipants'

function fmtMinor (minor, currency = 'USD') {
  return formatMinorCurrency(Number(minor) || 0, currency)
}

function wrapToWidth (str, width) {
  const s = String(str || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const words = s.split(/\s+/)
  const lines = []
  let cur = ''
  for (const w of words) {
    if (!w) continue
    const next = cur ? `${cur} ${w}` : w
    if (next.length <= width) {
      cur = next
    } else {
      if (cur) lines.push(cur)
      if (w.length <= width) {
        cur = w
      } else {
        for (let i = 0; i < w.length; i += width) {
          lines.push(w.slice(i, i + width))
        }
        cur = ''
      }
    }
  }
  if (cur) lines.push(cur)
  return lines.length ? lines : ['']
}

function padBetween (left, right, width) {
  const L = String(left)
  const R = String(right)
  const space = width - L.length - R.length
  if (space <= 0) return `${L} ${R}`
  return `${L}${' '.repeat(space)}${R}`
}

function resolveEntryCustomerLabel (entry, tab, participants = []) {
  const customerId = entry.customer_id || entry.metadata?.customer_id
  if (entry.customer) {
    const name = [entry.customer.first_name, entry.customer.last_name].filter(Boolean).join(' ').trim()
    if (name) return name
  }
  if (!customerId) return ''
  const match = participants.find(p => p.customer_id === customerId)
  if (match?.customer) {
    const name = [match.customer.first_name, match.customer.last_name].filter(Boolean).join(' ').trim()
    if (name) return name
  }
  return `Customer #${customerId}`
}

function activeEntries (entries) {
  return (Array.isArray(entries) ? entries : []).filter(e => !e.voided_at)
}

function resolveEntryChargeDate (entry) {
  const raw = entry?.occurred_at || entry?.created_at
  if (!raw) return null
  const d = new Date(raw)
  return Number.isNaN(d.getTime()) ? null : d
}

function chargeDateSortKey (date) {
  if (!date) return 'undated'
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * @param {Date|null} date
 */
export function formatTabStatementChargeDateLabel (date) {
  if (!date) return ''
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

/**
 * @param {object[]|null|undefined} entries
 * @returns {{ key: string, date: Date|null, entries: object[] }[]}
 */
export function groupTabStatementEntriesByChargeDate (entries) {
  const sorted = [...activeEntries(entries)].sort((a, b) => {
    const da = resolveEntryChargeDate(a)?.getTime() ?? Number.MAX_SAFE_INTEGER
    const db = resolveEntryChargeDate(b)?.getTime() ?? Number.MAX_SAFE_INTEGER
    if (da !== db) return da - db
    return (Number(a.sequence) || Number(a.id) || 0) - (Number(b.sequence) || Number(b.id) || 0)
  })

  /** @type {{ key: string, date: Date|null, entries: object[] }[]} */
  const groups = []
  const indexByKey = new Map()

  for (const entry of sorted) {
    const date = resolveEntryChargeDate(entry)
    const key = chargeDateSortKey(date)
    if (!indexByKey.has(key)) {
      const group = { key, date, entries: [] }
      indexByKey.set(key, groups.length)
      groups.push(group)
    }
    groups[indexByKey.get(key)].entries.push(entry)
  }

  return groups
}

function thermalGroupRule (cols, char = '-') {
  return `${char.repeat(Math.min(cols, 42))}\r\n`
}

function appendChargeDateGroupHeader (ops, op, addLines, cols, L, group, isFirst) {
  if (!isFirst) {
    ops.push(op({ type: 'text', text: '\r\n' }))
  }
  ops.push(op({ type: 'text', text: thermalGroupRule(cols, '*') }))
  const chargedLabel = L.chargedOn || 'Charged On'
  const dateLabel = group.date
    ? formatTabStatementChargeDateLabel(group.date)
    : (L.undatedCharges || 'Undated charges')
  addLines(`${chargedLabel}: ${dateLabel}`, { thick: 1 })
  ops.push(op({ type: 'text', text: thermalGroupRule(cols, '-') }))
}

function appendChargeDateGroupFooter (ops, op, cols) {
  ops.push(op({ type: 'text', text: thermalGroupRule(cols, '-') }))
}

/**
 * @param {object} params
 * @param {object} params.tab
 * @param {object} params.summary
 * @param {object[]|null|undefined} params.entries
 * @param {object[]|null|undefined} params.participants
 * @param {object|null} params.businessInfo
 * @param {object} opts
 */
export function buildVolcoraThermalTabStatementOps ({ tab, summary, entries, participants, businessInfo }, opts) {
  const cols = getThermalColumns(opts?.paperWidthMm ?? 80)
  const L = opts?.labels || {}
  const currency = tab?.currency || businessInfo?.currency || 'USD'
  const op = (o) => o
  const ops = []
  const printedAt = opts?.printedAt instanceof Date ? opts.printedAt : new Date()

  const addLines = (text, fontProps = {}) => {
    for (const ln of wrapToWidth(text, cols)) {
      ops.push(op({ type: 'text', text: `${ln}\r\n`, ...fontProps }))
    }
  }

  ops.push(op({ type: 'align', value: 1 }))
  if (opts?.logoBmpBase64) {
    ops.push(op({ type: 'bmpDirect', dataBase64: opts.logoBmpBase64 }))
    ops.push(op({ type: 'text', text: '\r\n' }))
  }
  const businessName = businessInfo?.name || businessInfo?.business_name
  if (businessName) {
    addLines(businessName, { font: 0, doubleWidth: true, doubleHeight: true, thick: 1 })
  }
  ops.push(op({ type: 'align', value: 0 }))
  if (businessInfo?.address) addLines(businessInfo.address)
  if (businessInfo?.phone) {
    addLines(`${L.phone || 'Phone'}: ${businessInfo.phone}`)
  }

  ops.push(op({ type: 'text', text: `${'-'.repeat(Math.min(cols, 42))}\r\n` }))
  addLines(`${L.tabStatement || 'Tab Statement'} #${tab?.id || ''}`)
  if (tab?.opened_at) {
    addLines(`${L.startedOn || 'Started On'}: ${new Date(tab.opened_at).toLocaleString()}`)
  }
  if (opts?.ownerLabel) {
    addLines(`${L.owner || 'Owner'}: ${opts.ownerLabel}`)
  }
  addLines(`${L.statementPrintedOn || L.date || 'Date'}: ${printedAt.toLocaleString()}`)

  ops.push(op({ type: 'text', text: '\r\n' }))

  const dateGroups = groupTabStatementEntriesByChargeDate(entries)
  dateGroups.forEach((group, groupIndex) => {
    appendChargeDateGroupHeader(ops, op, addLines, cols, L, group, groupIndex === 0)
    for (const entry of group.entries) {
      const lineMinor = Number(entry.amount_minor || 0) + Number(entry.tax_minor || 0)
      const qty = formatQuantityDisplay(entry.quantity ?? 1, {
        allowFractional: entry.allow_fractional ?? null
      })
      const desc = entry.description || entry.entry_type || 'Entry'
      addLines(`${qty} x ${fmtMinor(lineMinor, currency)}`)
      for (const nl of wrapToWidth(desc, cols)) {
        addLines(nl)
      }
      const customerLabel = resolveEntryCustomerLabel(entry, tab, participants)
      if (tabShowCustomerColumn(participants) && customerLabel) {
        addLines(customerLabel)
      }
      ops.push(op({ type: 'text', text: '\r\n' }))
    }
    appendChargeDateGroupFooter(ops, op, cols)
  })

  ops.push(op({ type: 'text', text: `${'-'.repeat(Math.min(cols, 42))}\r\n` }))

  const chargesMinor = Number(summary?.charges_minor || 0)
  const creditsMinor = Number(summary?.credits_minor || 0)
  const paymentsMinor = Number(summary?.payments_minor || 0)
  const currentTotalMinor = Number(summary?.current_total_minor || 0)
  const balanceMinor = Number(summary?.balance_minor || 0)
  const creditOnTabMinor = tabCreditOnTabMinorFromSummary(summary)

  ops.push(op({
    type: 'text',
    text: `${padBetween(L.charges || 'Charges', fmtMinor(chargesMinor, currency), cols)}\r\n`
  }))
  if (creditsMinor > 0) {
    ops.push(op({
      type: 'text',
      text: `${padBetween(L.credits || 'Credits', `- ${fmtMinor(creditsMinor, currency)}`, cols)}\r\n`
    }))
  }
  ops.push(op({
    type: 'text',
    text: `${padBetween(L.netCharges || 'Net charges', fmtMinor(currentTotalMinor, currency), cols)}\r\n`
  }))
  if (paymentsMinor > 0) {
    ops.push(op({
      type: 'text',
      text: `${padBetween(L.payments || 'Payments', `- ${fmtMinor(paymentsMinor, currency)}`, cols)}\r\n`
    }))
  }

  ops.push(op({ type: 'text', text: `${'-'.repeat(Math.min(cols, 42))}\r\n` }))

  if (balanceMinor > 0) {
    ops.push(op({
      type: 'text',
      text: `${padBetween(L.balanceDue || 'BALANCE DUE', fmtMinor(balanceMinor, currency), cols)}\r\n`,
      doubleWidth: true,
      thick: 1
    }))
    addLines(L.balanceDueHint || 'Amount not paid yet.')
  } else if (creditOnTabMinor > 0) {
    const creditLabel = (L.creditDisplay || 'Cr {amount}').replace('{amount}', fmtMinor(creditOnTabMinor, currency))
    ops.push(op({
      type: 'text',
      text: `${padBetween(L.creditBalance || 'CREDIT', creditLabel, cols)}\r\n`,
      doubleWidth: true,
      thick: 1
    }))
  } else {
    ops.push(op({
      type: 'text',
      text: `${padBetween(L.noBalanceDue || 'NO BALANCE DUE', fmtMinor(0, currency), cols)}\r\n`,
      doubleWidth: true,
      thick: 1
    }))
  }

  ops.push(op({ type: 'text', text: '\r\n\r\n' }))
  return ops
}

export { activeEntries as tabStatementActiveEntries }
